import React, { ChangeEvent, Component } from 'react';
import {
  BundleJson,
  BundlesPageJson,
  BundleStatus,
  CourierJson,
  InstructionType,
} from '../../types/bundleTypes';
import { Region } from '../../types/orderTypes';
import './BundlesList.scss';
import PlannerBundle from '../oldPlanner/planner/PlannerBundle';
import { fetchBundle, fetchBundlesPage } from './components/BundleUtils';
import { fetchAllCouriers, fetchCourierByIdList } from '../../fetch/fetchUsers';
import { Loader } from '../../components/Loader/Loader';
import { subscribeToChannel, unsubscribeFromChannel } from '../../utils/pusher';
import {
  bundleChannelSubscription,
  bundleEventName,
} from '../../const/pusherChannelEventConsts';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa';
import _ from 'lodash';
import Modal from '../../components/modals/Modal';
import MovingOrdersToBundleModalContent, {
  onTriggerMoveOrdersToSelectedBundle,
} from '../oldPlanner/planner/MovingOrdersToBundleModalContent';
import Swal from 'sweetalert2';
import { ComboBoxComponent } from '../../ReusableComponents/ComboBox/ComboBoxComponent';
import {
  ComboBoxItem,
  EventReason,
} from '../../ReusableComponents/ComboBox/ComboBoxComponent.types';
import { ButtonComponent } from '../../ReusableComponents/Button/ButtonComponent';

enum PlateId {
  COURIER = 'COURIER',
  VEHICLE = 'VEHICLE',
  SHELF = 'SHELF',
  DEPARTURE = 'DEPARTURE',
  PICKUPS = 'PICKUPS',
  ZONES = 'ZONES',
  BUNDLE = 'BUNDLE',
  DELIVERIES = 'DELIVERIES',
  DEVIATIONS = 'DEVIATIONS',
}

enum PlateTitle {
  COURIER = 'COURIER',
  VEHICLE = 'VEHICLE',
  SHELF = 'SHELF',
  DEPARTURE = 'DEPARTURE',
  PICKUPS = 'PICK UPS',
  ZONES = 'ZONES',
  BUNDLE = 'BUNDLE',
  DELIVERIES = 'DELIVERIES',
  DEVIATIONS = 'DEVIATIONS',
  DATE = 'DATE',
}

enum SortPatternForStatus {
  FINISHED = -1,
  ON_TIME = 1,
  DELAY = 2,
  UNKNOWN = 3,
}

interface Props {
  statusList: BundleStatus[];
  region: Region;
  completedStatusList?: boolean;
  autoSort?: boolean;
}

interface State {
  currentPage: number;
  couriers: { [courierId: string]: CourierJson };
  bundlesPage: BundlesPageJson;
  selectedBundleIds: string[];
  mapFocusedBundleId?: string;
  loadingScreen: boolean;
  listOfBundlesToUpdate: string[];
  selectedCourierIds: string[];
  allCouriers: CourierJson[];
  sortObject: { sortKey: PlateId | '' | 'STATUS'; sortType: string };
  sortedBundles: BundleJson[] | [];
  // eslint-disable-next-line @typescript-eslint/ban-types
  currentStatusOfBundles: { [key: string]: string } | {};
  // eslint-disable-next-line @typescript-eslint/ban-types
  assignedCourierBundleList: { [key: string]: string } | {};
  isOpenMovingBundleModal: boolean;
  bundleIdMoveFrom: string;
  selectedBundleToMoveOrders: string;
}

export class BundlesList extends Component<Props, State> {
  private readonly debouncedSort: (key, sortType) => void;
  private readonly plannerBundleListRefs: {
    [bundleId: string]: React.RefObject<PlannerBundle>;
  };
  private zonesOfBundles: { [bundleId: string]: string };
  constructor(props: Props) {
    super(props);
    this.state = {
      bundlesPage: {
        items: [],
        page: 0,
        pageSize: 0,
        total: 0,
        totalPages: 0,
      },
      loadingScreen: true,
      currentPage: 1,
      couriers: {},
      selectedBundleIds: [],
      mapFocusedBundleId: null,
      listOfBundlesToUpdate: [],
      selectedCourierIds: [],
      allCouriers: [],
      sortObject: { sortKey: '', sortType: '' },
      sortedBundles: [],
      currentStatusOfBundles: {},
      assignedCourierBundleList: {},
      isOpenMovingBundleModal: false,
      bundleIdMoveFrom: '',
      selectedBundleToMoveOrders: '',
    };
    this.debouncedSort = _.debounce(this.sortAction, 1100);
    this.plannerBundleListRefs = {};
    this.zonesOfBundles = {};
  }

  componentDidMount = () => {
    subscribeToChannel(bundleChannelSubscription, bundleEventName, data => {
      this.updateTriggerForPusher(data.bundleId);
    });
    this.loadData().then(() => null);
  };

  componentDidUpdate(
    prevProps: Readonly<Props>,
    prevState: Readonly<State>
  ): void {
    const { region } = this.props;
    const { currentPage } = this.state;
    if (region !== prevProps.region || currentPage !== prevState.currentPage) {
      this.loadDataWithLoader();
    }
  }

  componentWillUnmount(): void {
    unsubscribeFromChannel(bundleChannelSubscription);
  }

  clearUpdateQueue = (bundleId: string) => {
    const { listOfBundlesToUpdate } = this.state;
    if (
      listOfBundlesToUpdate.length !== 0 &&
      listOfBundlesToUpdate.includes(bundleId)
    ) {
      this.setState(
        {
          listOfBundlesToUpdate: listOfBundlesToUpdate.filter(
            id => bundleId !== id
          ),
        },
        () => this.updateBundle(bundleId)
      );
    }
  };

  updateTriggerForPusher = (bundleId: string) => {
    const { listOfBundlesToUpdate } = this.state;
    if (!listOfBundlesToUpdate.includes(bundleId)) {
      this.setState({
        listOfBundlesToUpdate: [...listOfBundlesToUpdate, bundleId],
      });
    }
  };

  loadDataWithLoader = () => {
    this.setState({ loadingScreen: true }, () => this.loadData());
  };

  loadData = async () => {
    let statusList: BundleStatus[];
    const { completedStatusList, region } = this.props;
    const { currentPage, loadingScreen, selectedCourierIds, sortObject } =
      this.state;
    if (completedStatusList) {
      statusList = [BundleStatus.Completed];
    } else {
      statusList = [
        BundleStatus.Published,
        BundleStatus.Proposed,
        BundleStatus.Accepted,
      ];
    }
    const bundlesPage = await fetchBundlesPage(
      statusList,
      region,
      completedStatusList ? currentPage : null,
      selectedCourierIds,
      !completedStatusList
    );
    this.setState({ bundlesPage: bundlesPage }, () => {
      if (sortObject.sortKey) {
        this.debouncedSort(sortObject.sortKey, sortObject.sortType);
      }
    });
    if (completedStatusList) {
      await this.fetchAllCouriersList(bundlesPage.items);
    } else {
      await this.fetchCouriers(bundlesPage.items);
    }
    if (loadingScreen) this.setState({ loadingScreen: false });
  };

  async fetchCouriers(bundles: BundleJson[]) {
    const courierIds = bundles
      .filter(bundle => !!bundle.assignedCourierId)
      .map(bundle => Number(bundle.assignedCourierId));
    const couriersTable = await fetchCourierByIdList(courierIds);
    if (!couriersTable) {
      return;
    }
    if (this.state.allCouriers.length === 0) {
      let currentStatusOfBundles = {};
      bundles.forEach(
        bundle =>
          (currentStatusOfBundles = {
            ...currentStatusOfBundles,
            [bundle.id]: 'UNKNOWN',
          })
      );
      const assignedCourierBundleList = {};
      bundles.forEach(
        bundle =>
          (assignedCourierBundleList[bundle.id] = bundle.assignedCourierName)
      );
      this.setState({
        assignedCourierBundleList,
        currentStatusOfBundles,
        couriers: couriersTable,
        allCouriers: Object.keys(couriersTable).map(id => couriersTable[id]),
      });
    } else {
      this.setState({ couriers: couriersTable });
    }
  }

  fetchAllCouriersList = async (bundles: BundleJson[]) => {
    const allCouriers = await fetchAllCouriers();
    const couriersTable = {};
    bundles
      .filter(bundle => !!bundle.assignedCourierId)
      .map(bundle => Number(bundle.assignedCourierId))
      .forEach(courierId => {
        couriersTable[courierId.toString()] = allCouriers.find(
          courier => courier.id === courierId
        );
      });
    this.setState({ allCouriers: allCouriers, couriers: couriersTable });
  };

  fetchCurrentStatusForSort = (
    statusOfBundle: {
      [bundleId: string]: string;
    },
    reSortStatus?: boolean
  ) => {
    const { currentStatusOfBundles, sortObject } = this.state;
    const { autoSort } = this.props;
    this.setState(
      {
        currentStatusOfBundles: {
          ...currentStatusOfBundles,
          ...statusOfBundle,
        },
      },
      () => {
        if (autoSort && sortObject.sortKey === 'STATUS' && reSortStatus) {
          this.debouncedSort(sortObject.sortKey, sortObject.sortType);
        }
      }
    );
  };

  setZonesOfBundles = (newZonesOfBundles: { [bundleId: string]: string }) => {
    this.zonesOfBundles = {
      ...this.zonesOfBundles,
      ...newZonesOfBundles,
    };
  };

  updateBundle = async (bundleId: string) => {
    const { bundlesPage, couriers, sortObject } = this.state;
    const updatedBundle = await fetchBundle(bundleId);
    const copyOfBundlesPage = Object.assign({}, bundlesPage);

    copyOfBundlesPage.items = copyOfBundlesPage.items.map(bundle =>
      bundle.id === bundleId ? updatedBundle : bundle
    );

    if (
      updatedBundle?.assignedCourierId &&
      !Object.keys(couriers).includes(updatedBundle.assignedCourierId)
    ) {
      const couriersTable = await fetchCourierByIdList([
        Number(updatedBundle.assignedCourierId),
      ]);
      this.setState(
        {
          bundlesPage: copyOfBundlesPage,
          couriers: { ...couriers, ...couriersTable },
        },
        () => {
          if (sortObject.sortKey) {
            this.debouncedSort(sortObject.sortKey, sortObject.sortType);
          }
        }
      );
    } else {
      this.setState({ bundlesPage: copyOfBundlesPage }, () => {
        if (sortObject.sortKey) {
          this.debouncedSort(sortObject.sortKey, sortObject.sortType);
        }
      });
    }
  };

  changePage = (offset: number) => {
    const nextPage = this.state.currentPage + offset;
    if (nextPage < 1 || nextPage > this.state.bundlesPage.totalPages) {
      return;
    } else {
      this.setState({
        currentPage: nextPage,
        sortObject: { sortKey: '', sortType: '' },
        sortedBundles: [],
      });
    }
  };

  onSelectBundle = (bundle: BundleJson, expanded: boolean) => {
    if (expanded) {
      this.setState({
        selectedBundleIds: [...this.state.selectedBundleIds, bundle.id],
      });
    } else {
      this.setState({
        selectedBundleIds: this.state.selectedBundleIds.filter(
          bundleId => bundleId !== bundle.id
        ),
      });
    }
  };

  openModalToSelectBundle = (bundleId: string) => {
    this.setState({
      isOpenMovingBundleModal: true,
      bundleIdMoveFrom: bundleId,
    });
  };

  onSelectBundleToMove = (bundleId: string) =>
    this.setState({ selectedBundleToMoveOrders: bundleId });

  closeModalOfMovingBundle = () =>
    this.setState({
      isOpenMovingBundleModal: false,
      selectedBundleToMoveOrders: '',
    });

  triggerMoveOrders = async () => {
    const { bundleIdMoveFrom, selectedBundleToMoveOrders } = this.state;
    const currentBundle = this.plannerBundleListRefs[bundleIdMoveFrom].current;
    const resp = await onTriggerMoveOrdersToSelectedBundle(
      bundleIdMoveFrom,
      selectedBundleToMoveOrders,
      currentBundle
    );
    if (typeof resp === 'boolean') {
      return;
    }
    if (resp) {
      currentBundle.clearSelectedOrdersAndStopMoving();
      await Swal.fire({
        position: 'center',
        icon: 'success',
        title: `Orders moved to the bundle ${selectedBundleToMoveOrders}`,
        showConfirmButton: false,
        timer: 2000,
      });
      await this.loadData();
      this.setState({
        isOpenMovingBundleModal: false,
        bundleIdMoveFrom: '',
        selectedBundleToMoveOrders: '',
      });
    } else {
      await Swal.fire({
        position: 'center',
        icon: 'error',
        title: `Something went wrong. Selected orders ${selectedBundleToMoveOrders} didn't move to the bundle ${selectedBundleToMoveOrders}`,
        showConfirmButton: false,
        timer: 3000,
      });
      this.setState({
        isOpenMovingBundleModal: false,
        bundleIdMoveFrom: '',
        selectedBundleToMoveOrders: '',
      });
    }
  };

  createBundlesRefs = () => {
    const { bundlesPage } = this.state;
    const bundles = bundlesPage.items;
    bundles.forEach(
      bundle => (this.plannerBundleListRefs[bundle.id] = React.createRef())
    );
  };

  handleDropdownChange = (
    event: ChangeEvent,
    selectedValues: ComboBoxItem[],
    eventReason: EventReason
  ): void => {
    if (eventReason === 'clear') {
      this.setState(
        {
          selectedCourierIds: [],
        },
        () => this.loadDataWithLoader()
      );
    } else {
      this.setState({
        selectedCourierIds: selectedValues.map(courier => courier.value),
      });
    }
  };

  sortBy = key => {
    const { sortObject } = this.state;
    const { sortKey, sortType } = sortObject;
    if (key === 'STATUS' && sortKey === '') {
      this.sortAction(key, 'desc');
      this.setState({ sortObject: { sortKey: key, sortType: 'desc' } });
    } else if (sortKey === key) {
      if (sortType === 'asc') {
        this.sortAction(key, 'desc');
        this.setState({ sortObject: { ...sortObject, sortType: 'desc' } });
      } else if (sortType === 'desc') {
        this.sortAction(key, 'clear');
        this.setState({ sortObject: { sortKey: '', sortType: '' } });
      }
    } else {
      this.sortAction(key, 'asc');
      this.setState({ sortObject: { sortKey: key, sortType: 'asc' } });
    }
  };

  sortSwitchAccess = key => {
    switch (key) {
      case 'COURIER':
        return 'assignedCourierName';
      case 'BUNDLE':
        return 'id';
      case 'SHELF':
        return 'shelfId';
      case 'DEPARTURE':
        return 'departure';
      case 'STATUS':
        return 'status';
      case 'ZONES':
        return 'zones';
      case 'PICKUPS':
        return 'pickups';
      default:
        return 'not support';
    }
  };

  calculatePickupSortCriterion = bundle => {
    if (bundle) {
      const pickupInstructions = bundle.instructions.filter(
        instr => instr.type === InstructionType.Pickup
      );
      const total = pickupInstructions.length;
      const completed = pickupInstructions.filter(
        pickupInstruction => pickupInstruction.completed
      ).length;
      return { difference: total - completed, total: total };
    }
  };

  sortAction = (key, sortType) => {
    const { bundlesPage, currentStatusOfBundles, assignedCourierBundleList } =
      this.state;
    const bundles = bundlesPage.items;
    if (sortType === 'clear') {
      this.setState({ sortedBundles: [] });
    } else {
      const objKey = this.sortSwitchAccess(key);
      if (objKey === 'not support') {
        return;
      } else if (objKey === 'status') {
        const sortedArrayOfBundles = new Array(bundles.length);
        const sortedStatus = Object.keys(currentStatusOfBundles)
          .sort((firstKey, secondKey) => {
            const firstValue =
              +SortPatternForStatus[currentStatusOfBundles[firstKey]];
            const secondValue =
              +SortPatternForStatus[currentStatusOfBundles[secondKey]];
            return (
              secondValue - firstValue ||
              assignedCourierBundleList[firstKey].localeCompare(
                assignedCourierBundleList[secondKey]
              )
            );
          })
          .map(currentStatus => currentStatus);
        bundles.forEach(
          bundle =>
            (sortedArrayOfBundles[
              sortedStatus.findIndex(sorted => sorted === bundle.id)
            ] = bundle)
        );
        this.setState({ sortedBundles: sortedArrayOfBundles });
      } else {
        this.setState({
          sortedBundles: Array.from(bundles).sort((a, b) => {
            let firstValue = '';
            let secondValue = '';
            if (objKey === 'departure') {
              firstValue = a?.instructions[0]?.eta?.format('HH:mm')
                ? a?.instructions[0]?.eta?.format('HH:mm')
                : firstValue;
              secondValue = b?.instructions[0]?.eta?.format('HH:mm')
                ? b?.instructions[0]?.eta?.format('HH:mm')
                : secondValue;
            } else if (objKey === 'zones') {
              firstValue = this.zonesOfBundles[a.id] || '';
              secondValue = this.zonesOfBundles[b.id] || '';
            } else if (objKey === 'pickups') {
              const first = this.calculatePickupSortCriterion(a);
              const second = this.calculatePickupSortCriterion(b);
              let result = first.difference - second.difference;
              result = result === 0 ? first.total - second.total : result;
              return sortType === 'asc' ? result : -result;
            } else {
              firstValue = a[objKey] ? a[objKey] : firstValue;
              secondValue = b[objKey] ? b[objKey] : secondValue;
            }
            if (sortType === 'asc') {
              return firstValue.localeCompare(secondValue);
            } else {
              return secondValue.localeCompare(firstValue);
            }
          }),
        });
      }
    }
  };

  paginationHeader = () => {
    const { currentPage, bundlesPage } = this.state;
    const bundlesPrPage = (currentPage - 1) * bundlesPage.pageSize;
    const bundles = bundlesPage.items;
    return (
      <>
        <div className="bundles-pagination-header">
          <span>
            Showing {bundlesPrPage + 1}-{bundlesPrPage + bundles.length}{' '}
            {bundlesPage.total > bundles.length && `(of  ${bundlesPage.total}`}
          </span>
          <span>
            <span
              onClick={() => this.changePage(-1)}
              style={{ cursor: 'pointer', fontWeight: 500 }}
            >
              Previous
            </span>
            <span style={{ margin: '0 20px 0 20px' }}>
              page {currentPage} of {bundlesPage.totalPages}
            </span>
            <span
              onClick={() => this.changePage(1)}
              style={{ cursor: 'pointer', fontWeight: 500 }}
            >
              Next
            </span>
          </span>
        </div>
      </>
    );
  };

  renderTitles = (
    completedStatusList: boolean,
    sortKey: string,
    sortType: string
  ) =>
    Object.keys(PlateId).map(key => {
      let onClick = null,
        title = PlateTitle[key],
        sortArrow = null;
      if (key === PlateId.DEPARTURE && completedStatusList) {
        title = PlateTitle.DATE;
      }
      if (sortKey === key && sortType === 'asc') {
        sortArrow = <FaArrowUp />;
      } else if (sortKey === key && sortType === 'desc') {
        sortArrow = <FaArrowDown />;
      }
      if (
        key === PlateId.COURIER ||
        key === PlateId.BUNDLE ||
        key === PlateId.DEPARTURE ||
        key === PlateId.SHELF ||
        key === PlateId.ZONES ||
        key === PlateId.PICKUPS
      ) {
        onClick = () => this.sortBy(key);
      }
      return (
        <div
          key={key}
          onClick={onClick}
          className={`planner-bundle-plate-${PlateId[key].toLowerCase()}-title`}
        >
          <span>
            {title} {sortArrow}
          </span>
        </div>
      );
    });

  render() {
    const {
      mapFocusedBundleId,
      loadingScreen,
      listOfBundlesToUpdate,
      allCouriers,
      bundlesPage,
      sortedBundles,
      sortObject,
      isOpenMovingBundleModal,
      selectedBundleToMoveOrders,
      bundleIdMoveFrom,
    } = this.state;
    const { completedStatusList, autoSort } = this.props;
    const { sortKey, sortType } = sortObject;
    let sourceData: BundleJson[] = [];
    if (loadingScreen) {
      return <Loader size="large" />;
    }
    if (sortedBundles.length > 0) {
      sourceData = sortedBundles;
    } else {
      sourceData = bundlesPage.items;
    }
    this.createBundlesRefs();
    let plateStatusClassName = 'planner-bundle-plate-status';
    if (sortKey === 'STATUS' && sortType === 'desc') {
      plateStatusClassName += ' desc';
    }
    return (
      <div className="bundles-list">
        {isOpenMovingBundleModal ? (
          <Modal
            onClose={this.closeModalOfMovingBundle}
            buttons={[{ title: 'Select', onClick: this.triggerMoveOrders }]}
          >
            <MovingOrdersToBundleModalContent
              bundleList={sourceData.filter(
                bundle => bundle.id !== bundleIdMoveFrom
              )}
              selectedBundle={selectedBundleToMoveOrders}
              onSelectBundle={this.onSelectBundleToMove}
            />
          </Modal>
        ) : null}
        <div className="courier-filter">
          <ComboBoxComponent
            id="courier-filter-monitor"
            comboBoxItems={allCouriers.map(courier => ({
              name: courier.fullName,
              value: courier.id.toString(),
            }))}
            comboBoxName="Select couriers..."
            multiple
            externalValuesHandler
            value={allCouriers
              .filter(courier =>
                this.state.selectedCourierIds.includes(courier.id.toString())
              )
              .map(courier => ({
                name: courier.fullName,
                value: courier.id.toString(),
              }))}
            onChange={this.handleDropdownChange as any}
          />
          <ButtonComponent
            text="Filter"
            color="secondary"
            onClick={() => this.loadDataWithLoader()}
          />
        </div>
        {completedStatusList ? this.paginationHeader() : null}
        <div>
          <div
            className={
              completedStatusList
                ? 'monitor-bundle-plate-container-completed'
                : 'monitor-bundle-plate-container'
            }
          >
            <div className="planner-bundle-plate-titles">
              {this.renderTitles(completedStatusList, sortKey, sortType)}
            </div>
            {!completedStatusList ? (
              <div
                onClick={() => this.sortBy('STATUS')}
                className={plateStatusClassName}
              />
            ) : null}
          </div>
          {sourceData.map(bundle => {
            return (
              <div
                key={bundle.id}
                className={
                  completedStatusList
                    ? 'bundles-list-grid-completed'
                    : 'bundles-list-grid'
                }
              >
                <PlannerBundle
                  ref={this.plannerBundleListRefs[bundle.id]}
                  autoSort={autoSort}
                  fetchCurrentStatus={this.fetchCurrentStatusForSort}
                  key={bundle.id}
                  bundle={bundle}
                  expanded={this.state.selectedBundleIds.includes(bundle.id)}
                  mapOnTop={bundle.id === mapFocusedBundleId}
                  onExpand={this.onSelectBundle}
                  onSavedModification={this.loadData}
                  onOrdersRemoved={this.loadData}
                  onMapSelected={() =>
                    this.setState({
                      mapFocusedBundleId: bundle.id,
                    })
                  }
                  bundleDraftIsSelected={false}
                  region={this.props.region}
                  monochrome={true}
                  monitorView={true}
                  completedStatusList={completedStatusList}
                  clearUpdateQueue={this.clearUpdateQueue}
                  listOfBundlesToUpdate={listOfBundlesToUpdate}
                  openModalToSelectBundle={this.openModalToSelectBundle}
                  setZonesOfBundles={this.setZonesOfBundles}
                  useItemBorder
                  itemColumns={[
                    'InstructionType',
                    'OrderNumber',
                    'Pin',
                    'Address',
                    'WindowTime',
                    'EstimateTimeArrival',
                    'Diff',
                    'Time',
                  ]}
                />
                {!completedStatusList ? (
                  <div className="bundles-list-status" />
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}
